import { FC, useState } from 'react';
import { Flex, Image } from '@chakra-ui/react';

import sepaImg from 'assets/brand/sepa@2x.png';
import Card from 'components/_core/Card';
import IconButton from 'components/_core/IconButton';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import { StepProps } from 'components/Stepper/types';
import Upload, { FILE_TYPE } from 'components/Upload';
import { UploadedFileInfo } from 'components/Upload/types';
import { getUploadDirectDebitMandateFunction } from 'features/Storage/upload';
import * as StepperLayout from 'layouts/StepperLayout';

import commonLocales from '../../common/locales';
import * as CommonStyles from '../../common/styles';
import NextButton from '../../components/NextButton';
import { useMandateCreationFlowContext } from '../../hooks/useMandateCreationFlowContext';
import locales from './locales';
import * as S from './styles';

const StepMandateDocumentUpload: FC<StepProps> = ({ goBack, goNext }) => {
  const { setSharedState, sharedState } = useMandateCreationFlowContext();
  const { mandate } = sharedState;

  const [file, setFile] = useState(mandate.file);

  const canSubmit =
    file.bucketName && file.fileName && file.fileRef && file.publicUrl;

  const onUploadSuccess = async (uploadedFile: UploadedFileInfo) => {
    setFile({
      bucketName: uploadedFile.bucketName,
      fileName: uploadedFile.fileName,
      fileRef: uploadedFile.fileRef,
      publicUrl: uploadedFile.url,
    });
  };

  const onDeleteFileClicked = () => {
    setFile({
      bucketName: '',
      fileName: '',
      fileRef: '',
      publicUrl: '',
    });
  };

  const onSubmit = () => {
    if (!canSubmit) {
      return;
    }

    setSharedState({
      ...sharedState,
      mandate: {
        ...mandate,
        file,
      },
    });
    goNext();
  };

  return (
    <StepperLayout.Root>
      <StepperLayout.Body>
        <CommonStyles.HeaderContainer>
          <BackButton onClick={goBack} />
        </CommonStyles.HeaderContainer>

        <StepperLayout.BodyScrollableSection>
          <S.Title>{locales.title}</S.Title>

          {file.fileRef ? (
            <S.MyMandateContainer>
              <S.MyMandateText>{locales.myMandate}</S.MyMandateText>
              <Card alignItems="center" flexDirection="row">
                <Flex flex={1}>
                  <Text fontWeight="weight-500">{file.fileName}</Text>
                </Flex>

                <IconButton
                  aria-label={locales.aria.see}
                  as="a"
                  href={file.publicUrl}
                  icon="eye-open"
                  marginRight="space-24"
                  target="_blank"
                  variant="secondary"
                />

                <IconButton
                  aria-label={locales.aria.delete}
                  icon="trash"
                  onClick={onDeleteFileClicked}
                  variant="secondary"
                />
              </Card>
            </S.MyMandateContainer>
          ) : (
            <Upload
              errorMessage={locales.uploadError}
              fileTypes={FILE_TYPE.ALLOWED_DOCUMENTS}
              onSuccessfulUpload={onUploadSuccess}
              uploadFile={getUploadDirectDebitMandateFunction(
                mandate.uniqueMandateReference,
              )}
            >
              <S.UploadAreaContainer>
                <Image
                  alt="upload illustration"
                  role="presentation"
                  src={sepaImg}
                />
                <S.UploadAreaText fontWeight="weight-500" size="lg">
                  {locales.uploadArea.title}
                </S.UploadAreaText>
                <S.UploadAreaText>
                  {locales.uploadArea.subTitle}
                </S.UploadAreaText>
              </S.UploadAreaContainer>
            </Upload>
          )}
        </StepperLayout.BodyScrollableSection>

        <CommonStyles.FooterContainer>
          <NextButton isDisabled={!canSubmit} onClick={onSubmit}>
            {commonLocales.nextButton}
          </NextButton>
        </CommonStyles.FooterContainer>
      </StepperLayout.Body>
    </StepperLayout.Root>
  );
};

export default StepMandateDocumentUpload;
